import { toast as toastify, ToastContentProps, Id, TypeOptions } from 'react-toastify';
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from 'assets/toastIcons';
import classNames from 'classnames';
import { XIcon } from 'lucide-react';

interface CustomMessageProps extends Partial<ToastContentProps> {
  title: string;
  content: string;
  ariaLabel?: string;
}

const defaultOptions = {
  autoClose: 5000,
  hideProgressBar: true,
  closeButton: false,
};

type CustomNotificationProps = ToastContentProps<{
  title: string;
  content: string;
}>;

interface INotificationStyling {
  titleColor: string;
  textColor: string;
  iconColor: string;
  backgroundColor: string;
  icon: React.ReactElement;
}

const getNotificationStyling = (type: TypeOptions): INotificationStyling => {
  switch (type) {
    case 'error': {
      return {
        titleColor: 'text-red-900',
        textColor: 'text-red-950',
        iconColor: 'text-red-500',
        backgroundColor: 'bg-red-100',
        icon: <ErrorIcon size={24} />,
      };
    }
    case 'success': {
      return {
        titleColor: 'text-green-900',
        textColor: 'text-green-950',
        iconColor: 'text-green-500',
        backgroundColor: 'bg-green-100',
        icon: <SuccessIcon size={24} />,
      };
    }
    case 'warning': {
      return {
        titleColor: 'text-yellow-900',
        textColor: 'text-yellow-950',
        iconColor: 'text-yellow-500',
        backgroundColor: 'bg-yellow-100',
        icon: <WarningIcon size={24} />,
      };
    }
    default:
    case 'info': {
      return {
        titleColor: 'text-blue-900',
        textColor: 'text-blue-950',
        iconColor: 'text-blue-500',
        backgroundColor: 'bg-blue-100',
        icon: <InfoIcon size={24} />,
      };
    }
  }
};

function CustomNotification({ closeToast, data, toastProps }: CustomNotificationProps) {
  const notificationStyling = getNotificationStyling(toastProps.type);

  return (
    <div
      className={classNames(
        'ml-1 flex w-full flex-row rounded-l-md p-4',
        notificationStyling.backgroundColor
      )}
    >
      <div className={notificationStyling.iconColor}>{notificationStyling.icon}</div>
      <div className="flex flex-1 flex-col pl-4">
        <h3 className={classNames('text-md font-semibold', notificationStyling.titleColor)}>
          {data.title}
        </h3>
        <div
          className={classNames('flex items-center justify-between', notificationStyling.textColor)}
        >
          <p className="text-sm">{data.content}</p>
        </div>
      </div>
      <div className="text-blue-900">
        <div className="-mt-4 -mr-4 p-4" role="button" onClick={closeToast}>
          <XIcon size={16} />
        </div>
      </div>
    </div>
  );
}

const Toast = (myProps: CustomMessageProps): Id =>
  toastify(CustomNotification, {
    ...defaultOptions,
    data: {
      title: myProps.title,
      content: myProps.content,
    },
    ariaLabel: myProps.ariaLabel ?? myProps.content,
    icon: false,
    type: 'info',
    className: '!bg-blue-500 p-0',
  });

Toast.success = (myProps: CustomMessageProps): Id =>
  toastify(CustomNotification, {
    ...defaultOptions,
    data: {
      title: myProps.title,
      content: myProps.content,
    },
    ariaLabel: myProps.ariaLabel ?? myProps.content,
    icon: false,
    type: 'success',
    className: '!bg-green-500 p-0',
  });

Toast.info = (myProps: CustomMessageProps): Id =>
  toastify(CustomNotification, {
    ...defaultOptions,
    data: {
      title: myProps.title,
      content: myProps.content,
    },
    ariaLabel: myProps.ariaLabel ?? myProps.content,
    icon: false,
    type: 'info',
    className: '!bg-blue-500 p-0',
  });

Toast.warning = (myProps: CustomMessageProps): Id =>
  toastify(CustomNotification, {
    ...defaultOptions,
    data: {
      title: myProps.title,
      content: myProps.content,
    },
    ariaLabel: myProps.ariaLabel ?? myProps.content,
    icon: false,
    type: 'warning',
    className: '!bg-yellow-500 p-0',
  });
Toast.error = (myProps: CustomMessageProps): Id =>
  toastify(CustomNotification, {
    ...defaultOptions,
    data: {
      title: myProps.title,
      content: myProps.content,
    },
    ariaLabel: myProps.ariaLabel ?? myProps.content,
    autoClose: false,
    icon: false,
    type: 'error',
    className: '!bg-red-500 p-0',
  });

export default Toast;
